import CwLoadModules from '@customjs/module-loader.js';

const CwThemeFramework = {
    async generalFns() {
        const office = document.querySelector('.select-office');
        if (office) {
            this.loadLocations(office);
            office.addEventListener('change', () => this.loadLocations(office));
        }

        //(For Contact page Location Widget)
        const officeContact = document.querySelector('.select-office-contact');
        if (officeContact) {
            this.loadLocationsContact(officeContact);
            officeContact.addEventListener('change', () => this.loadLocationsContact(officeContact));
        }

        this.stickyHeader();
        this.tocStickyScrollOffset();
    },

    /**
     * Optional pinned header (`enable_sticky_header` adds `.header--sticky` + sentinel in markup).
     * Header stays `position: absolute` until the sentinel scrolls away, then `.is-stuck` applies
     * `position: fixed` so the overlap design is preserved at the top of the page without `sticky`.
     */
    stickyHeader() {
        const header = document.querySelector('.header--sticky');
        if (!header) return;

        const sentinel = document.querySelector('.sticky-sentinel');
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                header.classList.toggle('is-stuck', !entry.isIntersecting);
            },
            { threshold: [0] }
        );

        observer.observe(sentinel);
    },

    /**
     * Offset TOC anchor scrolls so H2 titles sit below the sticky header.
     */
    tocStickyScrollOffset() {
        const extraGap = 16;
        const header = document.querySelector('.header--sticky');
        if (!header) return;

        const getStickyHeaderOffset = () => {
            const adminBar = document.getElementById('wpadminbar');
            let adminHeight = 0;

            if (adminBar) {
                const adminPosition = window.getComputedStyle(adminBar).position;
                if (adminPosition === 'fixed' || adminPosition === 'sticky') {
                    adminHeight = adminBar.offsetHeight || 0;
                }
            }

            if (header.classList.contains('is-stuck')) {
                return Math.ceil(header.getBoundingClientRect().bottom) + extraGap;
            }

            return Math.ceil(header.offsetHeight + adminHeight) + extraGap;
        };

        const applyScrollOffsetStyles = () => {
            const offset = getStickyHeaderOffset();
            const offsetValue = offset ? `${offset}px` : '';
            const tocVars = window.acf_toc_vars;
            let headings = document.querySelectorAll(
                '.has-main-toc #main-wrapper h2[id], .has-main-toc .entry-content h2[id]'
            );

            document.documentElement.style.scrollPaddingTop = offsetValue;

            if (!headings.length && tocVars && tocVars.main_content_container) {
                try {
                    headings = document.querySelectorAll(`${tocVars.main_content_container} h2[id]`);
                } catch (e) {
                    headings = [];
                }
            }

            Array.prototype.forEach.call(headings, (heading) => {
                heading.style.scrollMarginTop = offsetValue;
            });
        };

        const scrollToHeading = (id) => {
            const target = document.getElementById(id);
            if (!target) return false;

            const offset = getStickyHeaderOffset();
            const top = window.pageYOffset + target.getBoundingClientRect().top - offset;

            window.scrollTo({
                top: Math.max(0, top),
                behavior: 'smooth',
            });

            return true;
        };

        const onTocClick = (event) => {
            const link = event.target.closest('.toc-table-list a[href^="#"]');
            if (!link) return;

            const href = link.getAttribute('href');
            if (!href || href === '#') return;

            const id = decodeURIComponent(href.slice(1));
            if (!id || !document.getElementById(id)) return;

            event.preventDefault();
            applyScrollOffsetStyles();
            scrollToHeading(id);

            if (window.history && window.history.replaceState) {
                window.history.replaceState(null, '', window.location.pathname + window.location.search);
            }
        };

        applyScrollOffsetStyles();
        document.addEventListener('click', onTocClick);
        window.addEventListener('resize', applyScrollOffsetStyles);

        const observer = new MutationObserver(applyScrollOffsetStyles);
        observer.observe(header, { attributes: true, attributeFilter: ['class'] });

        // TOC plugin assigns heading IDs asynchronously.
        window.setTimeout(applyScrollOffsetStyles, 300);
        window.setTimeout(() => {
            const hash = window.location.hash;
            if (!hash || hash.length < 2) return;

            const id = decodeURIComponent(hash.slice(1));
            if (!document.getElementById(id)) return;

            applyScrollOffsetStyles();
            window.requestAnimationFrame(() => scrollToHeading(id));
        }, 350);
    },

    /**
     * Load Locations
     */
    async loadLocations(locationID) {
        if (locationID) {
            CwLoadModules.modals().then((modals) => {
                const officeHours = new modals('.cw-modal');
                officeHours.init();
            });
            const apiURL = `/api/location/${locationID.value}/`;
            const officeBox = document.querySelector('.office-info');
            const officeHours = document.querySelector('.opening-hours');
            const officeInfo = await fetch(apiURL);
            const officeDetails = await officeInfo.json();

            if (officeDetails.success) {
                const {
                    firmName,
                    directionURL,
                    city,
                    state,
                    zip,
                    location_address_1,
                    location_address_2,
                    phone,
                    email,
                    toll_free_number,
                    location_fax,
                    footer_map,
                    monday_hours,
                    tuesday_hours,
                    wednesday_hours,
                    thursday_hours,
                    friday_hours,
                    saturday_hours,
                    sunday_hours,
                } = officeDetails.data;

                const firmNameHTML = firmName ? `<div class="firm-name">${firmName}</div>` : '';
                const address1HTML = location_address_1 ?
                    `<span>${location_address_1}</span><br>` :
                    '';
                const address2HTML = location_address_2 ?
                    `<span>${location_address_2}</span><br>` :
                    '';
                const phoneHTML = phone ?
                    `<div class="phone">Phone: <a href="tel:${phone}" class="ctm-no-swap" aria-label="Phone">${phone}</a></div>` :
                    '';
                const faxHTML = location_fax ?
                    `<div class="fax">Fax: <a href="tel:${location_fax}" class="ctm-no-swap" aria-label="Fax">${location_fax}</a></div>` :
                    '';
                const emailHTML = email ?
                    `<a href="mailto:${email}" aria-label="Email Address">${email}</a>` :
                    '';
                const officeInfoButtons = `<div class="office-info__buttons">
                   <a href="${directionURL}" class="btn btn--tertiary" target="_blank" aria-label="Directions Link">Directions</a>
                   <button class="btn btn--tertiary cw-modal" data-cw-target="#hoursModal" id="office-hours">Office Hours</button>
               </div>`;

               const footerMapHTML = footer_map ?
                `<div class="office-map"><a href="${directionURL}" target="_blank" aria-label="Direction Link"><img src="${footer_map}" alt="${firmName}" /></a></div>` :
                '';

                const address = `${firmNameHTML}
               <address>
                   ${address1HTML}
                   ${address2HTML}
                   <span>${city}, ${state} ${zip}</span><br>
               </address>
               ${phoneHTML}
               ${faxHTML}
               ${emailHTML}${officeInfoButtons}
               ${footerMapHTML}`;
                officeBox.innerHTML = address;

                const mondayHoursHTML = monday_hours ?
                    `<tr><td class="day">Monday</td><td class="time"><span>${monday_hours}</span></td></tr>` :
                    '';
                const tuesdayHoursHTML = tuesday_hours ?
                    `<tr><td class="day">Tuesday</td><td class="time"><span>${tuesday_hours}</span></td></tr>` :
                    '';
                const wednesdayHoursHTML = wednesday_hours ?
                    `<tr><td class="day">Wednesday</td><td class="time"><span>${wednesday_hours}</span></td></tr>` :
                    '';
                const thursdayHoursHTML = thursday_hours ?
                    `<tr><td class="day">Thursday</td><td class="time"><span>${thursday_hours}</span></td></tr>` :
                    '';
                const fridayHoursHTML = friday_hours ?
                    `<tr><td class="day">Friday</td><td class="time"><span>${friday_hours}</span></td></tr>` :
                    '';
                const saturdayHoursHTML = saturday_hours ?
                    `<tr><td class="day">Saturday</td><td class="time"><span>${saturday_hours}</span></td></tr>` :
                    '';
                const sundayHoursHTML = sunday_hours ?
                    `<tr><td class="day">Sunday</td><td class="time"><span>${sunday_hours}</span></td></tr>` :
                    '';

                const hoursOffice = `<table>
                              <tbody>
                              ${mondayHoursHTML}
                              ${tuesdayHoursHTML}
                              ${wednesdayHoursHTML}
                              ${thursdayHoursHTML}
                              ${fridayHoursHTML}
                              ${saturdayHoursHTML}
                              ${sundayHoursHTML}
                              </tbody>
                            </table>`;

                // Hours Modal
                officeHours.innerHTML = hoursOffice;
            }
        }
    },

    async loadLocationsContact(contactLocationID) {
        if (contactLocationID) {
            CwLoadModules.modals().then((modals) => {
                const officeHours = new modals('.cw-modal');
                officeHours.init();
            });
            const apiURL = `/api/location/${contactLocationID.value}/`;
            const officeBox = document.querySelector('.office-info-contact');
            const officeHours = document.querySelector('.opening-hours');
            const officeInfo = await fetch(apiURL);
            const officeDetails = await officeInfo.json();

            if (officeDetails.success) {
                const {
                    firmName,
                    directionURL,
                    city,
                    state,
                    zip,
                    location_address_1,
                    location_address_2,
                    phone,
                    email,
                    toll_free_number,
                    location_fax,
                    footer_map,
                    monday_hours,
                    tuesday_hours,
                    wednesday_hours,
                    thursday_hours,
                    friday_hours,
                    saturday_hours,
                    sunday_hours,
                } = officeDetails.data;

                const firmNameHTML = firmName ? `<div class="firm-name">${firmName}</div>` : '';
                const address1HTML = location_address_1 ?
                    `<span>${location_address_1}</span><br>` :
                    '';
                const address2HTML = location_address_2 ?
                    `<span>${location_address_2}</span><br>` :
                    '';
                const phoneHTML = phone ?
                    `<div class="phone">Phone: <a href="tel:${phone}" class="ctm-no-swap" aria-label="Phone">${phone}</a></div>` :
                    '';
                const faxHTML = location_fax ?
                    `<div class="fax">Fax: <a href="tel:${location_fax}" class="ctm-no-swap" aria-label="Fax">${location_fax}</a></div>` :
                    '';
                const emailHTML = email ?
                    `<a href="mailto:${email}" aria-label="Email Address">${email}</a>` :
                    '';
                const officeInfoButtons = `<div class="office-info__buttons">
                   <a href="${directionURL}" class="btn btn--tertiary" target="_blank" aria-label="Directions Link">Directions</a>
                   <button class="btn btn--tertiary cw-modal" data-cw-target="#hoursModal" id="office-hours">Office Hours</button>
               </div>`;

               const footerMapHTML = footer_map ?
                `<div class="office-map"><a href="${directionURL}" target="_blank" aria-label="Direction Link"><img src="${footer_map}" alt="${firmName}" /></a></div>` :
                '';

                const address = `${firmNameHTML}
               <address>
                   ${address1HTML}
                   ${address2HTML}
                   <span>${city}, ${state} ${zip}</span><br>
               </address>
               ${phoneHTML}
               ${faxHTML}
               ${emailHTML}${officeInfoButtons}
               ${footerMapHTML}`;
                officeBox.innerHTML = address;

                const mondayHoursHTML = monday_hours ?
                    `<tr><td class="day">Monday</td><td class="time"><span>${monday_hours}</span></td></tr>` :
                    '';
                const tuesdayHoursHTML = tuesday_hours ?
                    `<tr><td class="day">Tuesday</td><td class="time"><span>${tuesday_hours}</span></td></tr>` :
                    '';
                const wednesdayHoursHTML = wednesday_hours ?
                    `<tr><td class="day">Wednesday</td><td class="time"><span>${wednesday_hours}</span></td></tr>` :
                    '';
                const thursdayHoursHTML = thursday_hours ?
                    `<tr><td class="day">Thursday</td><td class="time"><span>${thursday_hours}</span></td></tr>` :
                    '';
                const fridayHoursHTML = friday_hours ?
                    `<tr><td class="day">Friday</td><td class="time"><span>${friday_hours}</span></td></tr>` :
                    '';
                const saturdayHoursHTML = saturday_hours ?
                    `<tr><td class="day">Saturday</td><td class="time"><span>${saturday_hours}</span></td></tr>` :
                    '';
                const sundayHoursHTML = sunday_hours ?
                    `<tr><td class="day">Sunday</td><td class="time"><span>${sunday_hours}</span></td></tr>` :
                    '';

                const hoursOffice = `<table>
                              <tbody>
                              ${mondayHoursHTML}
                              ${tuesdayHoursHTML}
                              ${wednesdayHoursHTML}
                              ${thursdayHoursHTML}
                              ${fridayHoursHTML}
                              ${saturdayHoursHTML}
                              ${sundayHoursHTML}
                              </tbody>
                            </table>`;

                // Hours Modal
                officeHours.innerHTML = hoursOffice;
            }
        }
    },

    /**
     * Mobile Menu
     */
    mobileMenu() {
        const mobileButton = document.querySelector('.mobile-button');
        const mobileMenuElement = document.querySelector('#mmenu');
        const mobileChecker = document.querySelector('.mm-ocd');

        if (mobileButton && mobileMenuElement) {
            // Call mmenujs module
            CwLoadModules.mmenu().then((MmenuLight) => {
                if (!mobileChecker) {
                    const menu = new MmenuLight(mobileMenuElement);
                    menu.navigation();
                    const drawer = menu.offcanvas();

                    // Register event for listening to clicks
                    mobileButton.addEventListener('click', () => drawer.open());

                    /**
                     * Close Button
                     */
                    const closeButton = document.querySelector('.close__mobileMenu');

                    closeButton.addEventListener('click', () => drawer.close());

                    /**
                     *  Appending a click option for link tags
                     * with a hash(#) symbol at the beginning of the href
                     * */

                    const aHashTag = mobileMenuElement.querySelectorAll('a[href="#"]');
                    aHashTag.forEach((tag) => {
                        tag.addEventListener('click', (event) => {
                            event.target.parentNode.click();
                            event.preventDefault();
                        });
                    });
                }
            });
        }
    },
};

export default CwThemeFramework;